import { useCallback, useEffect, useState } from 'react';

/**
 * A stable identifier for one attempt at filling in a form.
 *
 * Minted when the dialog opens and resent unchanged on every retry, so a
 * double-click, a timeout the visitor retries, and a request the network
 * replayed all collapse onto a single stored event. A new one is minted only
 * once a submission has actually succeeded, which is the point at which the
 * next thing the visitor sends is genuinely a different submission.
 *
 * @param isOpen whether the dialog is currently open
 */
export interface ISubmissionId {
  /** Send this with every attempt. */
  submissionId: string;
  /** Call after a successful submit, so the next one is a new submission. */
  renew: () => void;
}

/** crypto.randomUUID needs a secure context; the fallback only needs to be unique. */
const mint = (): string => {
  const c = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

export const useSubmissionId = (isOpen: boolean): ISubmissionId => {
  const [submissionId, setSubmissionId] = useState(mint);

  useEffect(() => {
    if (isOpen) setSubmissionId(mint());
  }, [isOpen]);

  const renew = useCallback(() => setSubmissionId(mint()), []);

  return { submissionId, renew };
};
